import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { escalations } from '../api/client'

const POLL_INTERVAL = 15000 // 15 seconds
const TOAST_DURATION = 10000 // 10 seconds

const SEVERITY_CONFIG = {
  urgent: {
    bg: 'bg-red-900/95',
    border: 'border-red-500/60',
    icon: 'text-red-400',
    label: 'URGENT',
    labelBg: 'bg-red-500/30 text-red-300',
  },
  attention: {
    bg: 'bg-amber-900/95',
    border: 'border-amber-500/60',
    icon: 'text-amber-400',
    label: 'ATTENTION',
    labelBg: 'bg-amber-500/30 text-amber-300',
  },
  info: {
    bg: 'bg-dial-surface/95',
    border: 'border-lume-teal/40',
    icon: 'text-lume-teal',
    label: 'INFO',
    labelBg: 'bg-lume-teal/20 text-lume-teal',
  },
}

export default function EscalationToast() {
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [toasts, setToasts] = useState([])
  const lastSeenIdsRef = useRef(new Set())
  const initialLoadRef = useRef(true)

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  // Auto-dismiss toasts
  useEffect(() => {
    if (toasts.length === 0) return
    const timers = toasts.map(toast =>
      setTimeout(() => dismissToast(toast.id), TOAST_DURATION)
    )
    return () => timers.forEach(clearTimeout)
  }, [toasts, dismissToast])

  // Poll for new escalations
  useEffect(() => {
    if (!isAuthenticated) return

    async function checkEscalations() {
      try {
        const data = await escalations.list()
        const currentEscalations = data?.escalations || []
        const currentIds = new Set(currentEscalations.map(e => e.id))

        // On first load, just record existing IDs without showing toasts
        if (initialLoadRef.current) {
          lastSeenIdsRef.current = currentIds
          initialLoadRef.current = false
          return
        }

        // Find new escalations (IDs we haven't seen before)
        const newEscalations = currentEscalations.filter(
          e => !lastSeenIdsRef.current.has(e.id)
        )

        if (newEscalations.length > 0) {
          setToasts(prev => [
            ...prev,
            ...newEscalations.map(esc => ({
              id: esc.id,
              clientName: esc.clientName,
              summary: esc.summary,
              severity: esc.severity,
              escalationType: esc.escalationType,
              conversationId: esc.conversationId,
            })),
          ])
        }

        lastSeenIdsRef.current = currentIds
      } catch (err) {
        // Silently ignore polling errors
      }
    }

    checkEscalations()
    const interval = setInterval(checkEscalations, POLL_INTERVAL)
    return () => clearInterval(interval)
  }, [isAuthenticated])

  // Don't render if not authenticated or on login page
  if (!isAuthenticated || location.pathname === '/login') return null
  if (toasts.length === 0) return null

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-sm w-full pointer-events-none">
      {toasts.map(toast => {
        const config = SEVERITY_CONFIG[toast.severity] || SEVERITY_CONFIG.info
        return (
          <div
            key={toast.id}
            role="button"
            tabIndex={0}
            className={`pointer-events-auto ${config.bg} ${config.border} border rounded-xl shadow-2xl backdrop-blur-sm animate-slide-in cursor-pointer`}
            onClick={() => {
              dismissToast(toast.id)
              navigate(`/client-chats/${toast.conversationId}`)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                dismissToast(toast.id)
                navigate(`/client-chats/${toast.conversationId}`)
              }
            }}
          >
            <div className="p-4">
              <div className="flex items-start gap-3">
                {/* Bell icon */}
                <div className={`flex-shrink-0 mt-0.5 ${config.icon}`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-indices-bright">
                      {toast.clientName}
                    </span>
                    <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${config.labelBg}`}>
                      {config.label}
                    </span>
                  </div>
                  <p className="text-sm text-indices-medium line-clamp-2">
                    {toast.summary}
                  </p>
                  <p className="text-xs text-indices-muted mt-1">
                    Tap to view conversation
                  </p>
                </div>
                {/* Dismiss button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    dismissToast(toast.id)
                  }}
                  className="flex-shrink-0 p-1 text-indices-muted hover:text-indices-bright transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
